import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Stethoscope, CheckCircle, CreditCard, ArrowLeft, Shield, Lock, Eye, EyeOff } from 'lucide-react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';

// Initialize Stripe
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

// Card Element styling
const cardElementOptions = {
  style: {
    base: {
      fontSize: '16px',
      color: '#0f172a',
      fontFamily: "'Manrope', system-ui, sans-serif",
      '::placeholder': {
        color: '#94a3b8',
      },
      iconColor: '#0F766E',
    },
    invalid: {
      color: '#ef4444',
      iconColor: '#ef4444',
    },
  },
};

// Step 2: Payment Form Component (wrapped in Elements)
const PaymentForm = ({ formData, onBack, onComplete, loading, setLoading }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [cardError, setCardError] = useState('');
  const [cardComplete, setCardComplete] = useState(false);

  const handleCardChange = (event) => {
    setCardError(event.error ? event.error.message : '');
    setCardComplete(event.complete);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!stripe || !elements) {
      return;
    }

    if (!cardComplete) {
      setCardError('Please enter complete card details');
      return;
    }

    setLoading(true);
    setCardError('');

    try {
      // Step 1: Create account and get setup intent
      const signupResponse = await axios.post('/auth/signup-with-payment', {
        email: formData.email,
        password: formData.password,
        full_name: formData.full_name,
        user_level: formData.user_level
      });

      const { client_secret, token, user } = signupResponse.data;

      // Step 2: Confirm the setup intent with card details
      const { error: stripeError, setupIntent } = await stripe.confirmCardSetup(client_secret, {
        payment_method: {
          card: elements.getElement(CardElement),
          billing_details: {
            name: formData.full_name,
            email: formData.email,
          },
        },
      });

      if (stripeError) {
        setCardError(stripeError.message);
        setLoading(false);
        return;
      }

      if (setupIntent.status === 'succeeded') {
        // Step 3: Confirm payment method was saved
        await axios.post('/auth/confirm-payment-setup', {
          setup_intent_id: setupIntent.id,
          payment_method_id: setupIntent.payment_method
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });

        toast.success('🎉 Welcome! Your 7-day free trial has started.');
        onComplete(token, user);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to complete registration';
      
      // Check for existing email error
      if (errorMsg.toLowerCase().includes('already registered') || errorMsg.toLowerCase().includes('already exists')) {
        setCardError('This email is already registered. Please log in instead or use a different email.');
        toast.error('Email already registered! Try logging in or resetting your password.', { duration: 5000 });
      } else {
        setCardError(errorMsg);
        toast.error(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-2">
        <Label>Card Details</Label>
        <div className="p-4 border-2 border-slate-200 rounded-lg bg-white focus-within:border-[#0F766E] transition-colors">
          <CardElement options={cardElementOptions} onChange={handleCardChange} />
        </div>
        {cardError && (
          <p className="text-sm text-red-500">{cardError}</p>
        )}
      </div>

      <div className="p-3 rounded-lg bg-slate-50 border border-slate-200">
        <div className="flex items-center gap-2 text-sm">
          <Shield className="w-4 h-4 text-[#0F766E]" />
          <span style={{color: '#475569'}}>
            You won't be charged today. Your card will be saved for after your trial.
          </span>
        </div>
      </div>

      <div className="flex gap-3">
        <Button 
          type="button" 
          variant="outline" 
          onClick={onBack}
          className="flex-1 border-2 border-slate-300"
          disabled={loading}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <Button 
          type="submit" 
          className="flex-1 bg-[#0F766E] hover:bg-[#0d6960] text-white"
          disabled={loading || !stripe || !cardComplete}
        >
          <Lock className="w-4 h-4 mr-2" />
          {loading ? 'Processing...' : 'Start Free Trial'}
        </Button>
      </div>

      <p className="text-xs text-center" style={{color: '#64748b'}}>
        Your card will be charged $4.99/month after your 7-day trial unless you cancel.
      </p>
    </form>
  );
};

const AuthPage = ({ onLogin }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [step, setStep] = useState(1); // 1 = Account Info, 2 = Payment
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    user_level: 'Nursing Student'
  });
  const navigate = useNavigate();

  // Handle login
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post('/auth/login', {
        email: formData.email,
        password: formData.password
      });
      onLogin(response.data.token, response.data.user);
      toast.success('Welcome back!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  // Handle step 1 validation and proceed to step 2
  const handleAccountInfoSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.full_name || formData.full_name.trim().length < 2) {
      toast.error('Please enter your full name');
      return;
    }
    if (!formData.email || !formData.email.includes('@')) {
      toast.error('Please enter a valid email');
      return;
    }
    if (!formData.password || formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setStep(2);
  };

  // Handle successful signup completion
  const handleSignupComplete = (token, user) => {
    onLogin(token, user);
    navigate('/dashboard');
  };

  // Login Form
  if (isLogin) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 to-slate-100 dark:from-slate-900 dark:to-slate-950 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-3 mb-4">
              <Stethoscope className="w-10 h-10 text-primary" />
              <span className="text-3xl font-bold text-primary font-['Manrope']">ClinicalSim AI</span>
            </div>
            <h1 className="text-2xl font-semibold font-['Manrope']">Welcome Back</h1>
            <p className="mt-2" style={{color: '#1e293b', fontWeight: '500'}}>
              Log in to continue your practice
            </p>
          </div>

          <div className="medical-card p-8 !bg-white dark:!bg-slate-800" data-testid="auth-form" style={{backgroundColor: 'white'}}>
            <form onSubmit={handleLogin} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  data-testid="email-input"
                  id="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="you@example.com"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Input
                    data-testid="password-input"
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    required
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="••••••••"
                    className="pr-12"
                  />
                  <button
                    type="button"
                    data-testid="toggle-password-visibility"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 focus:outline-none"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <Button data-testid="submit-button" type="submit" className="w-full bg-[#0F766E] hover:bg-[#0F766E]/90 text-white" disabled={loading}>
                {loading ? 'Logging in...' : 'Log In'}
              </Button>
            </form>

            <div className="mt-6 text-center space-y-3">
              <button
                data-testid="toggle-auth-button"
                type="button"
                onClick={() => { setIsLogin(false); setStep(1); }}
                className="text-sm text-primary hover:underline block w-full"
              >
                Don't have an account? Start free trial
              </button>
              
              <button
                data-testid="forgot-password-link"
                type="button"
                onClick={() => navigate('/forgot-password')}
                className="text-sm text-muted-foreground hover:text-primary hover:underline block w-full"
              >
                Forgot your password?
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Signup Flow - Multi-step
  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 to-slate-100 dark:from-slate-900 dark:to-slate-950 flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 mb-4">
            <Stethoscope className="w-10 h-10 text-primary" />
            <span className="text-3xl font-bold text-primary font-['Manrope']">ClinicalSim AI</span>
          </div>
          <h1 className="text-2xl font-semibold font-['Manrope']">Start Your Free Trial</h1>
          <p className="mt-2" style={{color: '#1e293b', fontWeight: '500'}}>
            7 days free, then $4.99/month
          </p>
        </div>

        {/* Step Indicator */}
        <div className="flex items-center justify-center gap-3 mb-6">
          <div className={`flex items-center gap-2 ${step >= 1 ? 'text-[#0F766E]' : 'text-slate-400'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${step >= 1 ? 'bg-[#0F766E] text-white' : 'bg-slate-200 text-slate-500'}`}>
              1
            </div>
            <span className="text-sm font-medium hidden sm:inline">Account</span>
          </div>
          <div className={`w-12 h-0.5 ${step >= 2 ? 'bg-[#0F766E]' : 'bg-slate-200'}`}></div>
          <div className={`flex items-center gap-2 ${step >= 2 ? 'text-[#0F766E]' : 'text-slate-400'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${step >= 2 ? 'bg-[#0F766E] text-white' : 'bg-slate-200 text-slate-500'}`}>
              2
            </div>
            <span className="text-sm font-medium hidden sm:inline">Payment</span>
          </div>
        </div>

        {/* Trial Benefits */}
        <div className="mb-6 p-4 rounded-xl bg-gradient-to-r from-teal-50 to-cyan-50 border border-teal-200">
          <h3 className="font-semibold text-sm mb-3" style={{color: '#0F766E'}}>Your 7-Day Free Trial Includes:</h3>
          <ul className="space-y-2 text-sm">
            <li className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-[#0F766E]" />
              <span style={{color: '#334155'}}>1 AI-generated patient scenario</span>
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-[#0F766E]" />
              <span style={{color: '#334155'}}>Full simulation room access</span>
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-[#0F766E]" />
              <span style={{color: '#334155'}}>NANDA-I care plan tools</span>
            </li>
            <li className="flex items-center gap-2">
              <CreditCard className="w-4 h-4 text-[#0F766E]" />
              <span style={{color: '#334155'}}><strong>Auto-renews at $4.99/mo</strong> after trial</span>
            </li>
          </ul>
        </div>

        <div className="medical-card p-8 !bg-white dark:!bg-slate-800" data-testid="auth-form" style={{backgroundColor: 'white'}}>
          {step === 1 ? (
            // Step 1: Account Info
            <form onSubmit={handleAccountInfoSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="full_name">Full Name</Label>
                <Input
                  data-testid="full-name-input"
                  id="full_name"
                  type="text"
                  required
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  placeholder="John Doe"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  data-testid="email-input"
                  id="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="you@example.com"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Input
                    data-testid="password-input"
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    required
                    minLength={6}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="••••••••"
                    className="pr-12"
                  />
                  <button
                    type="button"
                    data-testid="toggle-password-visibility-signup"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 focus:outline-none"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                <p className="text-xs text-slate-500">Must be at least 6 characters</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="user_level">Experience Level</Label>
                <Select value={formData.user_level} onValueChange={(value) => setFormData({ ...formData, user_level: value })}>
                  <SelectTrigger data-testid="user-level-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Nursing Student">Nursing Student</SelectItem>
                    <SelectItem value="New Graduate Nurse">New Graduate Nurse</SelectItem>
                    <SelectItem value="Experienced Nurse">Experienced Nurse</SelectItem>
                    <SelectItem value="Transitioning Nurse">Transitioning Nurse</SelectItem>
                    <SelectItem value="Educator">Educator</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Button data-testid="continue-button" type="submit" className="w-full bg-[#0F766E] hover:bg-[#0F766E]/90 text-white">
                Continue to Payment →
              </Button>
            </form>
          ) : (
            // Step 2: Payment Info (wrapped in Stripe Elements)
            <Elements stripe={stripePromise}>
              <PaymentForm 
                formData={formData}
                onBack={() => setStep(1)}
                onComplete={handleSignupComplete}
                loading={loading}
                setLoading={setLoading}
              />
            </Elements>
          )}

          <div className="mt-6 text-center">
            <button
              data-testid="toggle-auth-button"
              type="button"
              onClick={() => { setIsLogin(true); setStep(1); }}
              className="text-sm text-primary hover:underline"
            >
              Already have an account? Log in
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;